import { VStack, Box, HStack, Text, Icon } from '@chakra-ui/react'
import { CheckIcon, TimeIcon } from '@chakra-ui/icons'
import type { TFunction } from 'i18next'
import type { OrderLifecycleTimelineEvent } from '@/types'

interface OrderLifecycleTimelineProps {
  events: OrderLifecycleTimelineEvent[]
  timelineLabelMap: Record<string, string>
  formatTimestamp: (value?: string | null, locale?: string) => string | null
  chainCompatibility?: string | null
  locale?: string
  t: TFunction
}

export const OrderLifecycleTimeline = ({
  events,
  timelineLabelMap,
  formatTimestamp,
  locale,
  t,
}: OrderLifecycleTimelineProps) => {
  const eventByType = new Map<string, OrderLifecycleTimelineEvent>()
  events.forEach(event => {
    if (!eventByType.has(event.type)) {
      eventByType.set(event.type, event)
    }
  })

  const baseEntries = Object.keys(timelineLabelMap).map(type => ({
    type,
    event: eventByType.get(type),
    achieved: eventByType.has(type),
  }))

  const extraEntries = events
    .filter(event => !timelineLabelMap[event.type])
    .map(event => ({
      type: event.type,
      event,
      achieved: true,
    }))

  const entries = [...baseEntries, ...extraEntries]

  return (
    <VStack align="stretch" spacing={3}>
      {entries.map(entry => {
        const translationKey =
          timelineLabelMap[entry.type] ?? `orders.lifecycle.timelineLabels.${entry.type}`
        const rawLabel = translationKey ? t(translationKey) : entry.type
        const resolvedLabel =
          translationKey && rawLabel === translationKey
            ? entry.type.replace(/_/g, ' ')
            : rawLabel.replace(/_/g, ' ')
        const timestamp = entry.event
          ? formatTimestamp(entry.event.timestamp, locale) ?? entry.event.timestamp
          : t('common.pending')

        return (
          <Box
            key={`${entry.type}-${entry.event?.timestamp ?? 'pending'}`}
            borderWidth="1px"
            borderRadius="md"
            p={3}
            borderColor={entry.achieved ? 'green.400' : 'gray.200'}
            bg={entry.achieved ? 'green.50' : 'transparent'}
          >
            <HStack justify="space-between" align="flex-start">
              <HStack spacing={2}>
                <Icon
                  as={entry.achieved ? CheckIcon : TimeIcon}
                  color={entry.achieved ? 'green.500' : 'gray.400'}
                  boxSize={4}
                />
                <Text fontWeight="medium" color={entry.achieved ? 'green.700' : 'gray.600'}>
                  {resolvedLabel}
                </Text>
              </HStack>
              <Text fontSize="sm" color={entry.achieved ? 'green.600' : 'gray.500'}>
                {timestamp}
              </Text>
            </HStack>
          </Box>
        )
      })}
    </VStack>
  )
}
